import { computed } from 'vue';
import { getSiteConfig, type SiteConfig } from '@/config/site.config';

export function useSiteConfig() {
  const config = getSiteConfig();

  // Computed properties for easy access
  const siteName = computed(() => config.siteName);
  const fullTitle = computed(() => config.fullTitle);
  const logo = computed(() => config.logo);
  const meta = computed(() => config.meta);
  const footer = computed(() => config.footer);
  const theme = computed(() => config.theme);
  const api = computed(() => config.api);

  // Helper functions
  const getPageTitle = (pageTitle?: string) => {
    if (!pageTitle) return config.fullTitle;
    return `${pageTitle} - ${config.siteName}`;
  };

  const getThemeColor = (colorName: keyof SiteConfig['theme']['colors']) => {
    return config.theme.colors[colorName];
  };

  const getBackground = (bgName: keyof SiteConfig['theme']['backgrounds']) => {
    return config.theme.backgrounds[bgName];
  };

  const getTextColor = (textType: keyof SiteConfig['theme']['text']) => {
    return config.theme.text[textType];
  };

  const getFont = (fontType: keyof SiteConfig['theme']['fonts']) => {
    return config.theme.fonts[fontType];
  };

  // Generate CSS variables for theme
  const getCSSVariables = () => {
    return {
      // Colors
      '--color-primary': config.theme.colors.primary,
      '--color-primary-light': config.theme.colors.primaryLight,
      '--color-primary-dark': config.theme.colors.primaryDark,
      '--color-secondary': config.theme.colors.secondary,
      '--color-accent': config.theme.colors.accent,
      '--color-recovery': config.theme.colors.recovery,

      // Backgrounds
      '--bg-primary': config.theme.backgrounds.primary,
      '--bg-secondary': config.theme.backgrounds.secondary,
      '--bg-dark': config.theme.backgrounds.dark,
      '--bg-gradient': config.theme.backgrounds.gradient,

      // Text
      '--text-primary': config.theme.text.primary,
      '--text-secondary': config.theme.text.secondary,
      '--text-light': config.theme.text.light,
      '--text-muted': config.theme.text.muted,

      // Fonts
      '--font-sans': config.theme.fonts.sans,
      '--font-heading': config.theme.fonts.heading
    };
  };

  return {
    // Full config
    config,

    // Computed properties
    siteName,
    fullTitle,
    logo,
    meta,
    footer,
    theme,
    api,

    // Helper functions
    getPageTitle,
    getThemeColor,
    getBackground,
    getTextColor,
    getFont,
    getCSSVariables
  };
}